
import { CommonModule } from '@angular/common';
import { Component, DOCUMENT, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { ProductsRepositoryService } from 'src/app/products/services/products-repository.service';
import { CUSTOM_OPTIONS, DynamicTemplateComponent } from '../../dynamic-template/dynamic-template.component';
import { ScrollingTextComponent } from "../../scrolling-text/scrolling-text.component";

enum ComponentType {
  NORMAL = 'Normal',
  CAROUSEL = 'Carousel',
  CAROUSEL_LIST = 'Carousel-List',
  CAROUSEL_LAYOUT = 'Carouse-layout',
  REVIEW_LAYOUT = 'Review-layout',
  SCROLL_TEXT = 'Scroll'
}

// Define comprehensive interface for banner data
interface BannerData {
  type: ComponentType;
  // Common properties
  htmlBaseCode?: string;
  navigationSection?: string;
  carouselCustomOptions?: OwlOptions;
  // CAROUSEL specific
  banners?: any[];
  navigationContainer?: string;
  carouselOptionUpdateObj?: any;
  // CAROUSEL_LIST specific
  sectionHeading?: string;
  productList?: any[]; // Array of products
  carouselListId?: string;
  countDown?: boolean;
  showCashpoint?: boolean;
  showAddToCart?: boolean;
  showPercentageDiscount?: boolean;
  collectionIds?: string[];
  categoriesInfo?: any[]; // Category information for side banner
  sideBannerProductList?: boolean; // Flag to show side banner
  sectionConfig?: any; // Section configuration
  // CAROUSEL_LAYOUT specific
  layoutData?: any[];
  carouselLayoutId?: string;
  carouselLayoutNavigationSection?: string;
  // REVIEW_LAYOUT specific
  reviewLayoutId?: string;
  layoutConfig?: any;
  // SCROLL_TEXT specific
  section?: string;
}

@Component({
  standalone: true,
  selector: 'base-primary-viewer-panel',
  templateUrl: './primary-viewer-panel.component.html',
  styleUrls: ['./primary-viewer-panel.component.scss'],
  imports: [CommonModule, DynamicTemplateComponent, ScrollingTextComponent]
})
export class PrimaryViewerPanelComponent {
  private productsRepositoryService = inject(ProductsRepositoryService);
  private document = inject<Document>(DOCUMENT);

  // Expose ComponentType enum for template usage
  readonly ComponentType = ComponentType;

  readonly index = input<number>(0);
  readonly page = input<string>('');

  readonly banner = input<any>(null);

  // Internal signal for processed banner data
  private _processedBanner = signal<BannerData | null>(null);
  readonly bannerData = this._processedBanner.asReadonly();

  // Track if banner is being processed to prevent infinite loops
  private _isProcessing = false;
  private _lastProcessedBanner: any = null;

  // Effect to process banner when it changes (like @Input() set in old code)
  constructor() {
    effect(() => {
      const banner = this.banner();

      // Only process if we have a banner and not currently processing
      if (banner && !this._isProcessing) {
        // Use a more robust comparison to prevent infinite loops
        const bannerString = JSON.stringify(banner);
        const lastBannerString = this._lastProcessedBanner ? JSON.stringify(this._lastProcessedBanner) : null;

        if (bannerString !== lastBannerString) {
          this._isProcessing = true;
          this._lastProcessedBanner = banner;

          // Use untracked to prevent this from triggering the effect again
          untracked(() => {
            this.processBanner(banner).finally(() => {
              this._isProcessing = false;
            });
          });
        }
      }
    });
  }

  private async processBanner(banner: any) {
    try {
      const processedBanner = await this.manageSection(banner);
      this._processedBanner.set(processedBanner);
    } catch (error) {
      this._processedBanner.set(null);
    }
  }



  // Computed signals for derived state
  readonly hasBanner = computed(() => !!this.banner());
  readonly bannerType = computed(() => this.bannerData()?.type);
  readonly isCarouselType = computed(() => this.bannerType() === ComponentType.CAROUSEL);
  readonly isCarouselListType = computed(() => this.bannerType() === ComponentType.CAROUSEL_LIST);
  readonly isCarouselLayoutType = computed(() => this.bannerType() === ComponentType.CAROUSEL_LAYOUT);
  readonly isReviewLayoutType = computed(() => this.bannerType() === ComponentType.REVIEW_LAYOUT);
  readonly isScrollTextType = computed(() => this.bannerType() === ComponentType.SCROLL_TEXT);
  readonly isNormalType = computed(() => this.bannerType() === ComponentType.NORMAL);

  private extractSectionId(section: any): string {
    if (typeof section === 'string' && section.includes('carousel-list')) {
      try {
        const tempDiv = this.document.createElement('div');
        tempDiv.innerHTML = section;
        const scriptElement = tempDiv.querySelector("script");
        if (scriptElement) {
          const script = JSON.parse(scriptElement.textContent ?? '');
          return script?.carouselListId || script?.id || 'unknown';
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }
    return section?.sectionId || section?.id || 'unknown';
  }

  getSectionId(bannerData: BannerData): string {
    switch (bannerData?.type) {
      case ComponentType.CAROUSEL_LIST:
        return bannerData?.carouselListId || '';
      case ComponentType.CAROUSEL_LAYOUT:
        return bannerData?.carouselLayoutId || '';
      case ComponentType.REVIEW_LAYOUT:
        return bannerData?.reviewLayoutId || '';
      default:
        return '';
    }
  }

  private processProductsForChild(rawProducts: any[]): any[] {
    if (!rawProducts || rawProducts.length === 0) {
      return [];
    }

    const processedProducts = rawProducts
      ?.filter((product: any) => !!product)
      ?.map((product: any) => {
        // Extract the actual product data from _source if it exists, otherwise use the product itself
        const sourceData = product?._source || product;
        const processedProduct = { ...sourceData };

        // Process mrp (round off)
        processedProduct.mrp = typeof (sourceData?.mrp) === 'string' ? sourceData?.mrp : this.roundOff(sourceData?.mrp ?? 0);

        // Process selling_price (round off)
        processedProduct.selling_price = this.roundOff(sourceData?.selling_price ?? 0);

        // Set stock properties for add-to-cart functionality
        processedProduct.in_stock = sourceData?.in_stock ?? true;
        processedProduct.is_allow_back_orders = sourceData?.is_allow_back_orders ?? false;

        return processedProduct;
      }) || [];

    return processedProducts;
  }

  private roundOff(value: number): number {
    return Math.round(value * 100) / 100;
  }

  async manageSection(section: any): Promise<BannerData | null> {
    const sectionId = this.extractSectionId(section);

    if (typeof section !== 'string' && !section?.sectionId?.includes('scroll')) {
      return null;
    }

    if (section.includes('carousel-container')) {
      if (section.includes("carousel-list")) {

        const tempDiv = this.document.createElement('div');
        tempDiv.innerHTML = section;
        const scriptElement = tempDiv.querySelector("script");
        if (scriptElement) {
          const script = JSON.parse(scriptElement.textContent ?? '');

          // Now use the parsed script object
          let productList: any[] = [];
          let carouselCustomOptions: OwlOptions = CUSTOM_OPTIONS;
          const {
            htmlBaseCode,
            dummyPreview,
            navigationSection,
            sectionHeading,
            id,
            productTag,
            collectionId,
            configuration,
            carouselListId,
            countDown = false,
            showCashpoint = false,
            showAddToCart = false,
            multiProductList,
            collectionIds,
            showPercentageDiscount = true,
            categoriesInfo,
            sideBannerProductList = false,
            sectionConfig
          } = script || {}

          if (!dummyPreview) {
            const { data } = configuration || {};

            // Always use API calls - no direct data extraction
            if (id?.length || productTag || collectionId || multiProductList) {
              const { noOfSlidesToShow = 4, noOfSlidesToShowInMobile = 1, margin = 10 } = configuration?.configuration || {};

              const carouselOptionUpdateObj = {
                autoplay: false,
                noOfSlidesToShowInMobile,
                ...(noOfSlidesToShow && { items: noOfSlidesToShow }),
                ...(margin && { margin })
              }

              // Make API calls and process data for child component
              if (id?.length) {
                await this.productsRepositoryService.getProductsByIds(id);
                const response = this.productsRepositoryService.productsByIds();
                const rawProducts = response?.hits?.hits || [];
                productList = this.processProductsForChild(rawProducts);
              }

              if (productTag) {
                const params = {
                  currentFilters: {
                    tags: productTag
                  },
                  pageNumber: 0,
                  pageSize: 1000,
                  search: '',
                  sortActive: '',
                  sortDirection: '',
                  ordering: '-created_at'
                };
                await this.productsRepositoryService.getProducts(params);
                const response = this.productsRepositoryService.products();
                const rawProducts = response?.hits?.hits || [];
                productList = this.processProductsForChild(rawProducts);
              }
              else if (collectionId) {
                const params = {
                  currentFilters: { collection_ids: collectionId },
                  pageNumber: 0,
                  pageSize: 1000,
                  search: '',
                  sortActive: '',
                  sortDirection: '',
                  ordering: '-created_at'
                };
                await this.productsRepositoryService.getProducts(params);
                const response = this.productsRepositoryService.products();
                const rawProducts = response?.hits?.hits || [];
                productList = this.processProductsForChild(rawProducts);
              }

              // If carouselOptionUpdateObj object present then updating carouselCustomOptions
              if (Object.keys(carouselOptionUpdateObj)?.length) {
                carouselCustomOptions = this.changeOptions(carouselOptionUpdateObj, carouselCustomOptions);
              }

              // Check for multiProductList (side-banner or multiple collections)
              if (multiProductList) {
                const validCollectionIds = collectionIds?.filter((id: any) => id);
                const validCategoriesInfo = categoriesInfo?.filter((info: any) => info);

                if (validCollectionIds?.length || validCategoriesInfo?.length) {
                  tempDiv.remove();
                  return {
                    type: ComponentType.CAROUSEL_LIST,
                    htmlBaseCode,
                    navigationSection,
                    carouselCustomOptions,
                    carouselListId,
                    countDown,
                    showCashpoint,
                    showAddToCart,
                    showPercentageDiscount,
                    collectionIds: validCollectionIds,
                    categoriesInfo: validCategoriesInfo,
                    sideBannerProductList,
                    sectionConfig
                  } as BannerData;
                }
              }
              tempDiv.remove();
              const bannerData = {
                type: ComponentType.CAROUSEL_LIST,
                sectionHeading,
                htmlBaseCode,
                productList: productList,
                navigationSection,
                carouselCustomOptions,
                carouselListId,
                countDown,
                showCashpoint,
                showAddToCart,
                showPercentageDiscount,
                collectionIds: data?.collectionIds || []
              } as BannerData;

              return bannerData;
            }
          }

        }
      } else if (section.includes("carousel-section")) {
        const tempDiv = this.document.createElement('div');
        tempDiv.innerHTML = section;
        const carouselSections = tempDiv.querySelectorAll('[id*=carousel_banner][class*=style-carousel_banner]');
        const navigationContainer = tempDiv.querySelector('[id*=carousal_navigation]');
        const carouselDotAvailable = tempDiv.getElementsByClassName('carousel-count-panel');
        let carouselOptionUpdateObj = {};

        if (carouselSections.length) {
          const banners: any[] = [];
          // Iterate through carousel sections and create components
          carouselSections.forEach((section: Element, index) => {
            section.removeAttribute("data-show-dots");
            const banner = section?.outerHTML;
            banners.push(banner);
          });

          if (carouselDotAvailable?.length) {
            carouselOptionUpdateObj = {
              dots: true
            }
          }

          return { type: ComponentType.CAROUSEL, banners, navigationContainer: navigationContainer?.outerHTML || '', carouselCustomOptions: carouselOptionUpdateObj } as BannerData;
        } else {
          // No carousel sections found, return null
          tempDiv.remove();
          return null;
        }
      } else if (section.includes("carousel-layout")) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = section;
        const scriptElement = tempDiv.querySelector("script");
        if (scriptElement) {
          const script = JSON.parse(scriptElement.textContent ?? "");
          const { sectionStyleClass, htmlBaseCode, layoutData, navigationSection, carousalLayoutDeviceConfig, carouselLayoutId } = script || {};
          tempDiv.remove();
          return { type: ComponentType.CAROUSEL_LAYOUT, layoutData, htmlBaseCode, navigationSection, carouselLayoutId } as BannerData;
        } else {
          tempDiv.remove();
          return null;
        }
      } else if (section.includes("review-layout")) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = section;
        const scriptElement = tempDiv.querySelector("script");
        if (scriptElement) {
          const script = JSON.parse(scriptElement.textContent ?? "");
          const { htmlBaseCode, navigationSection, reviewLayoutId, layoutConfig } = script || {};
          tempDiv.remove();
          return { type: ComponentType.REVIEW_LAYOUT, htmlBaseCode, navigationSection, reviewLayoutId, layoutConfig } as BannerData;
        } else {
          tempDiv.remove();
          return null;
        }
      }
    } else if (section?.sectionId?.includes('scroll')) {
      return { type: ComponentType.SCROLL_TEXT, section } as BannerData;
    } else {
      return { type: ComponentType.NORMAL, section } as BannerData;
    }

    // Fallback return for any unhandled cases
    return null;
  }


  changeOptions(updateProperty: any, previousOptions: OwlOptions) {
    const { items, ...rest } = updateProperty;
    let newProperty: Partial<OwlOptions>;
    if (items) {
      newProperty = {
        ...rest,
        responsive: {
          0: {
            items: 1
          },
          400: {
            items: 1
          },
          740: {
            items: 3
          },
          940: {
            items
          }
        },
      }
    } else {
      newProperty = {
        ...rest
      }
    }

    return { ...previousOptions, ...newProperty };
  }

}

